import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isTomorrow from "dayjs/plugin/isTomorrow";
import relativeTime from "dayjs/plugin/relativeTime";

import { useTalksZephrRsvp } from "lib/hooks/useTalksZephrRsvp";
import { useUserData } from "lib/store/user";
import type { TalkData } from "content-ui/talks/types";
import { CheckboxAndTerms } from "../checkboxAndTerms/checkboxAndTerms";
import { ErrorMessage } from "../errorMessage";
import { StaticTag } from "components/staticTag/staticTag";
import { talkEndTime } from "content-ui/talks/utils";
import { SponsorLogoAndText } from "components/eventCard/talkElements";
import { JoinButton } from "../buttons/joinButton";
import { RsvpButton } from "../buttons/rsvpButton";

dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(relativeTime);

const TalkRegistration = ({
  talk,
  allTalks,
  immediateJoin = false,
  variant = "eventCard",
}: {
  talk: TalkData;
  allTalks?: TalkData[];
  immediateJoin?: boolean;
  variant?: "eventCard" | "singlePage";
}) => {
  // RSVPing
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isCheckedOptional, setIsCheckedOptional] = useState<boolean>(false);
  const talksForHook = useMemo(
    () => (allTalks && allTalks.length > 0 ? allTalks : [talk]),
    [allTalks, talk]
  );
  const { isReady, hasRsvped, registerRsvp, recordTalkCheckIn } =
    useTalksZephrRsvp(talksForHook);
  const rsvped = hasRsvped(talk.id);
  const [rsvpSent, setRsvpSent] = useState<boolean>(rsvped);
  const [checkBoxError, setCheckBoxError] = useState(false);

  // Date & time
  const talkStartDateTime = dayjs(talk.dateTime);
  const talkEarliestJoinTime = talkStartDateTime.subtract(60, "minute");

  // Misc
  const { hasMissingInfo, readerSubscriptionLevel, isAuthorisedUser } =
    useUserData();
  const [isHydrated, setIsHydrated] = useState(false);

  // Wait until after client-side hydration to show
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  useEffect(() => {
    setRsvpSent(rsvped);
  }, [rsvped]);

  const handleRegistration = async () => {
    if (!isAuthorisedUser) {
      // Redirect unregistered users to login/register
      window.location.href = "/members/login";
      return;
    }

    if (hasMissingInfo) {
      window.location.href = "/members/complete-profile";
      return;
    }

    // Registered user with complete profile - process RSVP
    const marketingConsent = talk.checkboxText ? isChecked : null;
    const optionalConsent = talk.optionalCheckboxText
      ? isCheckedOptional
      : null;
    const registrationSuccessful = await registerRsvp(
      talk,
      marketingConsent,
      optionalConsent
    );

    if (registrationSuccessful) {
      setRsvpSent(true);
    }
  };

  const onClickRegister = () => {
    const showCheckboxError = talk.checkboxText && !isChecked;

    if (showCheckboxError) {
      setCheckBoxError(true);
    } else {
      handleRegistration();
    }
  };

  const onClickJoin = () => {
    void recordTalkCheckIn(talk);
    window.open(talk?.talkLink || "https://sifted.eu/talks");
  };

  const joinAvailable = () => {
    const currentTime = dayjs();
    if (now.getTime() >= talkEndTime(talk?.dateTime, talk?.duration))
      return false;
    if (currentTime >= talkEarliestJoinTime || immediateJoin) return true;
    return false;
  };

  const Divider = () => {
    const customDashedClasses = `h-[1px]
[background-image:_linear-gradient(to_right,_var(--mono-30),_var(--mono-30)_2px,_transparent_2px,_transparent_6px)]
[background-size:_6px_2px]`;

    return (
      <div
        className={classNames("my-5", {
          [customDashedClasses]: variant === "eventCard",
          hidden: variant === "singlePage",
        })}
      ></div>
    );
  };

  const now = new Date();
  const isTalkEnded =
    now.getTime() >= talkEndTime(talk?.dateTime, talk?.duration);

  if (!isHydrated || isTalkEnded) return null;
  if (!isReady) return null;

  return (
    <div
      className={classNames("flex w-full flex-col", {
        "lg:flex-row": variant === "eventCard",
        "gap-y-5 md:flex-row md:gap-x-6 lg:gap-y-0": variant === "singlePage",
      })}
    >
      {!rsvpSent && <Divider />}

      {!rsvpSent ? (
        <div className="flex flex-col md:flex-row md:gap-x-6">
          {talk.sponsorLogo && variant === "eventCard" ? (
            <div className="min-w-[146px] max-md:mb-2 lg:hidden">
              <SponsorLogoAndText
                image={talk.sponsorLogo}
                sponsorDetails={talk.sponsorDetails}
              />
            </div>
          ) : null}

          <div className="flex flex-col">
            <CheckboxAndTerms
              talk={talk}
              setIsChecked={setIsChecked}
              setIsCheckedOptional={setIsCheckedOptional}
            />
            <div className="ml-7 mt-[6px]">
              {checkBoxError && !isChecked ? <ErrorMessage /> : null}
            </div>
          </div>
        </div>
      ) : null}

      <div
        className={classNames("flex justify-center gap-4 lg:w-full", {
          "mt-5 items-center lg:mt-0 lg:justify-end": variant === "eventCard",
          "items-start lg:mt-0 lg:justify-end":
            variant === "singlePage" && !rsvpSent,
          "items-start lg:mt-0 lg:justify-start":
            variant === "singlePage" && rsvpSent,
        })}
      >
        {rsvpSent ? (
          joinAvailable() ? (
            <JoinButton onClickJoin={onClickJoin} />
          ) : (
            !isTalkEnded && (
              <div
                className={classNames("flex w-full ", {
                  "items-end justify-center md:justify-end lg:min-h-[62px]":
                    variant === "eventCard",
                })}
              >
                <StaticTag
                  noWrap={false}
                  className={classNames("lg:min-w-[160px]")}
                  text="You'll receive the event link in your inbox on the day."
                />
              </div>
            )
          )
        ) : (
          <RsvpButton
            talk={talk}
            onClickRegister={onClickRegister}
            rsvpSent={rsvpSent}
            userLevel={readerSubscriptionLevel}
          />
        )}
      </div>
    </div>
  );
};

export default TalkRegistration;
